import React, { useState, useEffect } from "react";
import "./style/css/LoginForm.css";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { HeaderLanding } from "./components/HeaderLanding";

// Define the shape of the form values
interface FormValues {
  email: string;
  password: string;
}

// Define the shape of the validation errors
type FormErrors = Partial<Record<keyof FormValues, string>>;

// --- LoginForm Component ---
export function LoginForm() {
  // Assuming useAuth provides a login function
  const { login } = useAuth();
  // Hook for navigation after successful login
  const navigate = useNavigate();

  const handleTryFree = () => {
    navigate("/#register");
  };

  // --- State Initialization ---
  const [values, setValues] = useState<FormValues>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // --- Handlers ---

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Clear any related validation error when the field changes
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    // Clear any general login error when the user starts typing
    setLoginError(null);
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = (currentValues: FormValues): FormErrors => {
    const nextErrors: FormErrors = {};

    if (!currentValues.email) {
      nextErrors.email = "El correo electrónico es obligatorio.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentValues.email)) {
      nextErrors.email = "Ingresa un email válido.";
    }

    if (!currentValues.password) {
      nextErrors.password = "La contraseña es obligatoria.";
    }

    return nextErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate(values);
    setErrors(validationErrors);

    // If there are client-side validation errors, stop
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setLoginError(null);

    // --- Authentication Logic (as provided) ---
    try {
      await login(values.email, values.password);
      // Navigate to the dashboard on successful login
      navigate("/dashboard");
    } catch (error: any) {
      // Set the generic error message on failure
      setLoginError(
        "Credenciales no válidas. Por favor, revisa tu email y contraseña.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Render ---
  return (
    <>
      <HeaderLanding onTryFree={handleTryFree} showLoginButton={false} />
      {/* Login Form Section */}
      <div className="login-page-container">
        <div className="login-auth-container">
          <div className="login-auth-card">
            <h3 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
              Iniciar Sesión
            </h3>

            {/* Conditional Error Message based on loginError state */}
            {loginError && (
              <div className="login-message-box login-message-error">
                <i className="fas fa-times-circle"></i>
                {/* Using the state variable to show the error */}
                {loginError}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="login-form-group">
                <label htmlFor="email">Correo Electrónico</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  required
                  placeholder="tu.nombre@ejemplo.com"
                />
                {errors.email && (
                  <div
                    style={{
                      color: "var(--error)",
                      marginTop: "0.5rem",
                      fontSize: "0.9rem",
                    }}
                  >
                    {errors.email}
                  </div>
                )}
              </div>

              <div className="login-form-group">
                <label htmlFor="password">Contraseña</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  required
                  placeholder="Ingresa tu contraseña"
                />
                {errors.password && (
                  <div
                    style={{
                      color: "var(--error)",
                      marginTop: "0.5rem",
                      fontSize: "0.9rem",
                    }}
                  >
                    {errors.password}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn--primary btn--large"
                style={{ width: "100%", marginTop: "1rem" }}
                disabled={isSubmitting}
              >
                <i
                  className={`fas fa-sign-in-alt ${
                    isSubmitting ? "fa-spinner fa-spin" : ""
                  }`}
                ></i>
                {isSubmitting ? "Accediendo..." : "Entrar"}
              </button>
            </form>

            <p
              className="text-center"
              style={{ marginTop: "1.5rem", fontSize: "0.95rem" }}
            >
              ¿Olvidaste tu contraseña? <a href="#">Recuperar</a>
            </p>
          </div>

          <p
            className="text-center"
            style={{
              marginTop: "var(--space-md)",
              fontSize: "0.9rem",
              color: "var(--color-secondary)",
            }}
          >
            ¿Aún no tienes cuenta? <a href="register.html">Regístrate aquí</a>
          </p>
        </div>
      </div>
    </>
  );
}

export default LoginForm;
